// The ramp onto a road bridge: a broad 2x2 stone abutment carrying the two-lane
// asphalt deck, with capped corner pillars. Ported 1:1 from age-of-mcp's
// road_bridge_head.ts: the shared foundation/scaffold come from the library, the
// pillar/deckTop/corner are local helpers, and the four build stages are
// transcribed from stage(s).
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, type Part } from "../../lib";

const STONE = "#8d8a82";
const STONE_DARK = "#6f6c64";
const ASPHALT = "#3b3b40";
const CAP = "#6f6c64";

function pillar(x: number, z: number, h: number): Part {
  return { geometry: new THREE.BoxGeometry(0.22, h, 0.22), color: STONE_DARK, position: [x, h / 2, z] };
}

function deckTop(h: number): Part[] {
  return [
    // The broad stone mass.
    { geometry: new THREE.BoxGeometry(1.8, h, 1.8), color: STONE, position: [0, h / 2, 0] },
    // Asphalt apron on top (the two lanes continue onto the deck).
    { geometry: new THREE.BoxGeometry(1.7, 0.05, 1.7), color: ASPHALT, position: [0, h + 0.03, 0] },
  ];
}

// A corner pillar topped with a darker capstone — symmetric on all four corners
// so they never block either span direction.
function corner(x: number, z: number, h: number): Part[] {
  return [
    pillar(x, z, h),
    { geometry: new THREE.BoxGeometry(0.3, 0.08, 0.3), color: CAP, position: [x, h, z] },
  ];
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(2.0, 0.1)];
  if (s === 1) return [...deckTop(0.18), ...scaffold(1.8, 0.24)];
  if (s === 2) return [...deckTop(0.34), ...scaffold(1.8, 0.4)];
  return [
    ...deckTop(0.42),
    ...corner(-0.8, -0.8, 0.62), ...corner(0.8, -0.8, 0.62),
    ...corner(-0.8, 0.8, 0.62), ...corner(0.8, 0.8, 0.62),
  ];
}

export const roadBridgeHead = defineModel({
  id: "road-bridge-head",
  tiles: { w: 2, h: 2 },
  batchId: "standard",
  name: "Road bridge head",
  tags: ["building", "road", "bridge", "head", "low-poly", "age-of-mcp", "classic", "water"],
  summary: "Low-poly road bridge head.",
  description: "A ramp onto a road bridge; a pair on opposite banks spans a gap with a two-lane deck for vehicles.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
